import { sum } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import { Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { EstimateInfo, EstimateMetrics } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { sortBy } from '@paragon/utilities';

interface EstimateMetricsProps {
  readonly estimateMetrics: EstimateMetrics;
  readonly estimateInfo: EstimateInfo;
  readonly distanceFormatter: (distanceInInches: number) => string;
}

export function EstimateMetricsList({ estimateInfo, estimateMetrics, distanceFormatter }: EstimateMetricsProps) {
  const aggregateTrussMetrics = useMemo(() => {
    let numberOfPlates = 0;
    let totalBoardFeet = 0;
    let totalLinearFeet = 0;
    const thicknessCounts: {
      thicknessInInches: number;
      componentCount: number;
      boardFeet: number;
      linearFeet: number;
    }[] = [];

    for (const [componentDesignGuid, quantity] of Object.entries(estimateInfo.componentDesigns)) {
      numberOfPlates += quantity * estimateMetrics.numberOfPlates[componentDesignGuid];
      let thicknessCountEntry = thicknessCounts.find((thicknessCount) =>
        distanceEquals(
          thicknessCount.thicknessInInches,
          estimateMetrics.componentDesignGuidToThickness[componentDesignGuid],
        ),
      );
      if (!thicknessCountEntry) {
        thicknessCountEntry = {
          thicknessInInches: estimateMetrics.componentDesignGuidToThickness[componentDesignGuid],
          componentCount: 0,
          boardFeet: 0,
          linearFeet: 0,
        };
        thicknessCounts.push(thicknessCountEntry);
      }
      const boardFeet = quantity * estimateMetrics.feetByComponentDesignGuid[componentDesignGuid].boardFeet;
      const linearFeet = quantity * estimateMetrics.feetByComponentDesignGuid[componentDesignGuid].linearFeet;
      totalBoardFeet += boardFeet;
      totalLinearFeet += linearFeet;
      thicknessCountEntry.componentCount += quantity;
      thicknessCountEntry.boardFeet += boardFeet;
      thicknessCountEntry.linearFeet += linearFeet;
    }

    return {
      numberOfPlates,
      totalBoardFeet,
      totalLinearFeet,
      thicknessCounts: sortBy(thicknessCounts, (thicknessCount) => thicknessCount.thicknessInInches),
    };
  }, [estimateInfo, estimateMetrics]);
  const [trussMetricsUnit, setTrussMetricsUnit] = useState<'BF' | 'LF'>('LF');

  return (
    <>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid size={10}>
          <Typography variant="h5">Estimate Metrics</Typography>
        </Grid>
        <Grid size={2}>
          <ToggleButtonGroup
            value={trussMetricsUnit}
            size="small"
            exclusive
            onChange={(_, trussMetricsUnit) => {
              if (trussMetricsUnit == null) return;
              setTrussMetricsUnit(trussMetricsUnit);
            }}
          >
            <ToggleButton value="BF">BF</ToggleButton>
            <ToggleButton value="LF">LF</ToggleButton>
          </ToggleButtonGroup>{' '}
        </Grid>
      </Grid>

      {aggregateTrussMetrics ? (
        <>
          <Typography>Distinct members: {estimateMetrics.numberOfDistinctMembers}</Typography>
          <Typography>Plates: {aggregateTrussMetrics.numberOfPlates}</Typography>
          {aggregateTrussMetrics.thicknessCounts.length > 1
            ? aggregateTrussMetrics.thicknessCounts.map((thicknessCount) => (
                <Typography key={thicknessCount.thicknessInInches}>
                  {distanceFormatter(thicknessCount.thicknessInInches)} Trusses: {thicknessCount.componentCount}{' '}
                  trusses,{' '}
                  {trussMetricsUnit === 'BF'
                    ? Math.round(thicknessCount.boardFeet)
                    : Math.round(thicknessCount.linearFeet)}{' '}
                  {trussMetricsUnit}
                </Typography>
              ))
            : null}
          <Typography>
            Total Trusses:{' '}
            {sum(aggregateTrussMetrics.thicknessCounts.map((thicknessCount) => thicknessCount.componentCount))} trusses,{' '}
            {trussMetricsUnit === 'BF'
              ? Math.round(aggregateTrussMetrics.totalBoardFeet)
              : Math.round(aggregateTrussMetrics.totalLinearFeet)}{' '}
            {trussMetricsUnit}
          </Typography>
        </>
      ) : null}
    </>
  );
}
